import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import { Box } from '@mui/system'
import axios from 'api/axios'
import {
  consultaInicial,
  executeFieldAction,
  selectCamposSolicitud,
  selectDatosSolicitud,
  selectDescripcionesSolicitud,
  setCampoValidando,
  setDatoSolicitud,
} from 'features/solicitudes/solicitudesSlice'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { validate } from '../../libs/FieldResquestValidator'
import {
  getValoresConsulta,
  processValidationResult,
  todosLosPadresTienenValor,
} from '../../libs/SolicitudesHelpers'
import LabelComponent from '../LabelComponent'
import ValidatingText from '../ValidatingText'

function ListComponent({ idpk }) {
  const dispatch = useDispatch()
  const campos = useSelector(selectCamposSolicitud)
  const campo = campos.filter((c) => c.idpk === idpk)[0]

  const datos = useSelector(selectDatosSolicitud)
  const valor = datos[campo.idpk]

  const descripciones = useSelector(selectDescripcionesSolicitud)
  const descripcion = descripciones[campo.idpk]

  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')

  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [lvLoading, setLvLoading] = useState(false)
  const [error, setError] = useState('')
  const [consultando, setConsultando] = useState(false)

  const getConsultaLOV = (inicializar = false, initialValue = null) => {
    if (!lvLoading) {
      setLvLoading(true)
      axios
        .post(`/consultalov/${idpk}/`, {
          valores: getValoresConsulta(datos, campos),
          busqueda: value,
        })
        .then((request_response) => {
          const data = request_response.data
          setOptions(data.registros ?? [])
          if (initialValue) {
            const opcion_seleccionada = data.registros?.filter((o) => o.codigo === initialValue)[0]
            if (opcion_seleccionada) {
              const opcionInicial = {
                codigo: initialValue,
                descripcion: opcion_seleccionada.descripcion,
              }
              setValue(opcionInicial)
              setInputValue(opcion_seleccionada.descripcion)
            } else {
              setValue(null)
              setInputValue('')
            }
          }
        })
        .catch((error) => {})
        .finally(() => {
          setLvLoading(false)
          if (inicializar) {
            dispatch(consultaInicial({ idpk: campo.idpk, valores: [] }))
          }
        })
    }
  }

  // La inicialización ahora se hace de forma centralizada en CamposSolicitud
  // mediante el endpoint /inicializasolicitud/

  useEffect(() => {
    const processValueChange = async () => {
      // Si hay valor, validar primero
      if (valor) {
        try {
          // Activar estado de validación
          dispatch(setCampoValidando({ idpk: campo.idpk, validando: true }))

          const result = await validate(campo, valor, campos, datos)
          processValidationResult(result, campo, valor, dispatch, descripcion)
        } catch (error) {
          // Si hay error de validación, limpiar el campo y no ejecutar el resto
          setValue(null)
          setInputValue('')
          dispatch(setDatoSolicitud({ idpk: campo.idpk, valor: null, descripcion: '' }))
          return
        } finally {
          // Desactivar estado de validación
          dispatch(setCampoValidando({ idpk: campo.idpk, validando: false }))
        }
      }

      // Lanzamos las queries iniciales para campos dependientes solo si:
      // 1. El campo tiene valor
      // 2. El campo dependiente necesita inicialización (inicializando === 'P')
      // 3. El campo dependiente NO está siendo inicializado por inicializaSolicitud (inicializando !== 'R')
      campos.forEach((c) => {
        if (
          c.depende_de &&
          c.depende_de.includes(campo.codigo_campo) &&
          valor &&
          c.inicializando === 'P' &&
          todosLosPadresTienenValor(c, campos, datos)
        ) {
          dispatch(consultaInicial({ idpk: c.idpk, valores: getValoresConsulta(datos, campos) }))
        }
      })

      if (value !== valor) {
        const opcion_seleccionada = options?.filter((o) => o.codigo === valor)[0]
        if (opcion_seleccionada) {
          const opcionInicial = {
            codigo: valor,
            descripcion: opcion_seleccionada.descripcion,
          }
          setValue(opcionInicial)
          setInputValue(opcion_seleccionada.descripcion)
        } else {
          if (!consultando) {
            setConsultando(true)
            getConsultaLOV(false, valor)
          }
          const opcionInicial = {
            codigo: valor,
            descripcion: descripcion,
          }
          setValue(opcionInicial)
          setInputValue(descripcion)
          setOptions([
            {
              id: 1,
              codigo: valor,
              descripcion: descripcion,
            },
          ])
        }
      }
      campo.acciones.forEach((action) => {
        if (
          action.valor_referencia === valor ||
          (action.valor_referencia === 'NOT_NULL' && valor) ||
          (action.valor_referencia === 'NULL' && !valor)
        ) {
          dispatch(executeFieldAction({ action }))
        }
      })
    }

    processValueChange()
    // eslint-disable-next-line
  }, [valor])

  useEffect(() => {
    if (campo.lovVersion > 0) {
      setOptions([])
      setValue(null)
      setInputValue('')
      getConsultaLOV()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campo.lovVersion])

  useEffect(() => {
    if (open) {
      getConsultaLOV()
    }
    // eslint-disable-next-line
  }, [open])

  const handleChange = (nuevoValor) => {
    dispatch(
      setDatoSolicitud({
        idpk: campo.idpk,
        valor: nuevoValor ? nuevoValor.codigo : null,
        descripcion: nuevoValor ? nuevoValor.descripcion : '',
      }),
    )
    setValue(nuevoValor)
  }

  if (campo) {
    return error ? (
      <Alert severity="error">Error cargando los datos</Alert>
    ) : (
      <Box sx={{ width: '100%' }}>
        <LabelComponent text={campo.etiqueta} obligatorio={campo.obligatorio} />
        <Autocomplete
          disablePortal
          value={value}
          onChange={(_, newValue) => {
            handleChange(newValue)
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue)
          }}
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, val) => {
            return option.codigo === valor
          }}
          getOptionLabel={(option) => option.descripcion ?? ''}
          options={options ?? []}
          loading={lvLoading}
          key={`list-${campo.idpk}`}
          id={campo.codigo_campo}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={campo.etiqueta}
              error={campo.error || error}
              InputLabelProps={value ? { shrink: true } : {}}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {lvLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={campo.informativo === 'S' || campo.validando}
        />
        {campo.validando && (
          <FormHelperText>
            <ValidatingText />
          </FormHelperText>
        )}
      </Box>
    )
  }
}

export default ListComponent
